/**
 * Domain detail body (DEV-121) — the full management view for a single domain,
 * rendered on its own page (`/domains/:domain`). Premium tabbed sections
 * (Overview / DNS / SSL / Deployment / Billing / Registrar) with an at-a-glance
 * summary and an animated status timeline.
 *
 * 100% data-driven — every value comes from the backend (`/api/domains/:id`). No
 * assumed/hardcoded metadata: a field/section is shown only when its real value is
 * present. Connected (BYO) domains get a lighter view.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { classNames } from '~/utils/classNames';
import { ConfirmationDialog } from '~/components/ui/Dialog';
import {
  apiGetDomain,
  apiSetDomainAutoRenew,
  apiRenewDomain,
  apiConnectDomain,
  apiDisconnectDomain,
  apiListDnsRecords,
  apiCreateDnsRecord,
  apiDeleteDnsRecord,
  apiListEmailForwards,
  apiCreateEmailForward,
  apiDeleteEmailForward,
  apiSetWhoisPrivacy,
  apiTransferOut,
  apiRelockDomain,
  apiCancelTransferIn,
  apiListDeployments,
  type ManagedDomain,
  type DomainDetail,
  type RegistrantInfo,
  type DomainDnsRecordInput,
} from '~/lib/api/domains';
import { apiCreatePaymentMethodUpdateSession, type PaymentMethodSummary } from '~/lib/api/billing';
import { CARD, StatusPill, TERMINAL, fmtMoney, fmtDate, fmtDateTime } from './domainUi';
import { usePlanAccess, handlePlanRequired, showPlans } from '~/lib/planGate';

type StepState = 'done' | 'current' | 'pending' | 'failed';

interface Step {
  key: string;
  label: string;
  hint: string;
  state: StepState;
  at?: string | null;
}

function buildSteps(d: DomainDetail): Step[] {
  const status = d.status;
  const paid = status !== 'pending';
  const registered = ['registered', 'active'].includes(status) || Boolean(d.registeredAt);
  const dns = status === 'active' || Boolean(d.connectedSubdomain);
  const connected = Boolean(d.connectedSubdomain);
  const ssl = d.sslStatus === 'active';
  const processing = status !== 'failed' && status !== 'pending';

  const raw: { key: string; label: string; hint: string; done: boolean; at?: string | null }[] = [
    { key: 'placed', label: 'Order placed', hint: 'Your domain order was created.', done: true, at: d.createdAt },
    { key: 'paid', label: 'Payment confirmed', hint: 'Payment received securely.', done: paid, at: d.paidAt },
    {
      key: 'registered',
      label: 'Domain registered',
      hint: 'Registered in your name with the registrar.',
      done: registered,
      at: d.registeredAt,
    },
    { key: 'dns', label: 'DNS configured', hint: 'Records pointed at Dunea’s edge.', done: dns },
    { key: 'connected', label: 'Connected to project', hint: 'Routed to your live deployment.', done: connected },
    { key: 'ssl', label: 'SSL provisioned', hint: 'HTTPS certificate issued.', done: ssl },
  ];

  let markedActive = false;

  return raw.map((s) => {
    if (s.done) {
      return { ...s, state: 'done' as StepState };
    }

    if (!markedActive) {
      markedActive = true;

      if (status === 'failed') {
        return { ...s, state: 'failed' as StepState };
      }

      return { ...s, state: (processing ? 'current' : 'pending') as StepState };
    }

    return { ...s, state: 'pending' as StepState };
  });
}

function StepRow({ step, last, index }: { step: Step; last: boolean; index: number }) {
  const icon =
    step.state === 'done'
      ? 'i-ph:check-bold text-success'
      : step.state === 'current'
        ? 'i-svg-spinners:90-ring-with-bg text-accent'
        : step.state === 'failed'
          ? 'i-ph:x-bold text-danger'
          : 'i-ph:circle text-faint';

  const ring =
    step.state === 'done'
      ? 'bg-success/10 ring-success/25'
      : step.state === 'current'
        ? 'bg-accent/10 ring-accent/30 shadow-[0_0_0_4px_rgba(217,242,106,0.06)]'
        : step.state === 'failed'
          ? 'bg-danger/10 ring-danger/25'
          : 'bg-white/[0.03] ring-subtle';

  return (
    <div
      className="flex gap-3.5 opacity-0 animate-[ui-slide-in-right_360ms_ease-out_forwards]"
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <div className="flex flex-col items-center">
        <div
          className={classNames(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1 ring-inset transition-all duration-300',
            step.state === 'current' && 'animate-pulse',
            ring,
          )}
        >
          <div className={classNames(icon, 'text-[14px]')} />
        </div>
        {!last && (
          <div
            className={classNames(
              'w-px flex-1 transition-colors',
              step.state === 'done' ? 'bg-success/25' : 'bg-hairline',
            )}
            style={{ minHeight: 24 }}
          />
        )}
      </div>
      <div className={classNames('min-w-0 pb-5', last && 'pb-0')}>
        <div
          className={classNames('text-[13.5px] font-medium', step.state === 'pending' ? 'text-muted' : 'text-primary')}
        >
          {step.label}
        </div>
        <div className="mt-0.5 text-[12px] leading-relaxed text-muted">{step.hint}</div>
        {step.at && step.state === 'done' && (
          <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-faint">
            <div className="i-ph:clock text-[11px]" />
            {fmtDateTime(step.at)}
          </div>
        )}
      </div>
    </div>
  );
}

/** A key/value row. Rendered only when `children` is provided by the caller. */
function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <span className="shrink-0 text-[12.5px] text-muted">{label}</span>
      {/* Long values (deploy URLs, registrar IDs) wrap instead of overflowing on phones. */}
      <span className="min-w-0 text-right text-[13px] text-secondary [overflow-wrap:anywhere]">{children}</span>
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <section className="animate-[ui-slide-in-right_240ms_ease-out]">
      <div className="mb-3 flex items-center gap-2">
        <div className={classNames(icon, 'text-[15px] text-accent')} />
        <h4 className="ui-caption">{title}</h4>
      </div>
      {children}
    </section>
  );
}

function FactTile({
  icon,
  label,
  value,
  tone = 'muted',
}: {
  icon: string;
  label: string;
  value: string;
  tone?: 'success' | 'accent' | 'muted' | 'warning';
}) {
  const valueTone =
    tone === 'success'
      ? 'text-success'
      : tone === 'accent'
        ? 'text-accent'
        : tone === 'warning'
          ? 'text-warning'
          : 'text-primary';
  const iconTone =
    tone === 'success'
      ? 'bg-success/10 text-success ring-success/20'
      : tone === 'accent'
        ? 'bg-accent/10 text-accent ring-accent/20'
        : tone === 'warning'
          ? 'bg-warning/10 text-warning ring-warning/20'
          : 'bg-white/[0.04] text-muted ring-subtle';

  return (
    <div className={classNames(CARD, 'group p-4 transition-all duration-300 can-hover:hover:-translate-y-0.5')}>
      <div
        className={classNames('mb-3 flex h-8 w-8 items-center justify-center rounded-lg ring-1 ring-inset', iconTone)}
      >
        <div className={classNames(icon, 'text-[15px]')} />
      </div>
      <div className={classNames('text-[14px] font-semibold', valueTone)}>{value}</div>
      <div className="mt-0.5 text-[11px] uppercase tracking-[0.08em] text-muted">{label}</div>
    </div>
  );
}

function CopyRow({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <button
      onClick={() => {
        navigator.clipboard?.writeText(value).then(
          () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
          },
          () => {},
        );
      }}
      className="inline-flex items-center justify-center gap-1.5 text-muted transition-colors hover:text-accent touch:h-10 touch:w-10"
      title="Copy"
      aria-label="Copy"
    >
      <div className={classNames(copied ? 'i-ph:check' : 'i-ph:copy', 'text-[13px]', copied && 'text-success')} />
    </button>
  );
}

type Tab = 'overview' | 'dns' | 'email' | 'ssl' | 'deployment' | 'billing' | 'registrar';

function apiErr(err: unknown, fallback: string): string {
  return (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;
}

/**
 * Email forwarding for a registered domain: aliases like hello@domain that
 * redirect to an inbox the user already has. Receive-only — a note makes the
 * one-directional nature explicit so nobody expects to send *from* the alias.
 */
function EmailForwardManager({ domain, domainId }: { domain: string; domainId: string }) {
  const queryClient = useQueryClient();
  const [box, setBox] = useState('');
  const [to, setTo] = useState('');
  // Deleting a forward stops mail immediately — confirm before it happens.
  const [pendingRemove, setPendingRemove] = useState<string | null>(null);

  const { data: forwards, isLoading } = useQuery({
    queryKey: ['domain-email', domainId],
    queryFn: () => apiListEmailForwards(domainId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['domain-email', domainId] });

  const createMutation = useMutation({
    mutationFn: () => apiCreateEmailForward(domainId, box.trim(), to.trim()),
    onSuccess: () => {
      invalidate();
      setBox('');
      setTo('');
      toast.success('Forwarding added.');
    },
    onError: (err: unknown) => toast.error(apiErr(err, 'Could not add the forward.')),
  });

  const deleteMutation = useMutation({
    mutationFn: (emailBox: string) => apiDeleteEmailForward(domainId, emailBox),
    onSuccess: () => {
      invalidate();
      toast.success('Forwarding removed.');
    },
    onError: (err: unknown) => toast.error(apiErr(err, 'Could not remove the forward.')),
  });

  return (
    <Section title="Email forwarding" icon="i-ph:envelope-simple">
      <div className={classNames(CARD, 'overflow-hidden')}>
        {isLoading && (
          <div className="flex items-center gap-2 px-5 py-4 text-[12.5px] text-muted">
            <div className="i-svg-spinners:90-ring-with-bg" />
            Loading forwards…
          </div>
        )}

        {forwards?.length === 0 && !isLoading && (
          <div className="px-5 py-6 text-center text-[12.5px] text-muted">
            No forwarding set up yet. Add an address below to start receiving mail at your domain.
          </div>
        )}

        {forwards?.map((f) => (
          <div
            key={f.emailBox}
            className="flex items-center gap-3 border-t border-hairline px-4 py-3.5 first:border-t-0 sm:px-5"
          >
            <div className="i-ph:envelope-simple shrink-0 text-[15px] text-accent" />
            {/* Stacks alias over destination on phones so neither truncates to nothing. */}
            <div className="ui-mono min-w-0 flex-1 text-[12.5px] sm:truncate">
              <span className="block truncate text-primary sm:inline">
                {f.emailBox}@{domain}
              </span>
              <span className="mx-1.5 hidden text-muted sm:inline">→</span>
              <span className="block truncate text-secondary sm:inline">
                <span className="text-muted sm:hidden">→ </span>
                {f.emailTo}
              </span>
            </div>
            <button
              onClick={() => setPendingRemove(f.emailBox)}
              disabled={deleteMutation.isPending}
              className="inline-flex shrink-0 items-center justify-center text-muted transition-colors hover:text-danger touch:h-10 touch:w-10"
              aria-label={`Remove forwarding for ${f.emailBox}@${domain}`}
              title="Remove forwarding"
            >
              <div className="i-ph:trash text-[13px]" />
            </button>
          </div>
        ))}

        {/* Add-forward row */}
        <div className="flex flex-wrap items-center gap-2 border-t border-hairline bg-white/[0.02] px-4 py-3.5 sm:px-5">
          <div className="flex min-w-48 flex-1 items-center rounded-lg border border-subtle bg-sunken">
            <input
              value={box}
              onChange={(e) => setBox(e.target.value)}
              placeholder="hello"
              aria-label="Mailbox name"
              className="min-w-0 flex-1 bg-transparent px-3 py-2 font-geist-mono text-[12.5px] text-primary outline-none placeholder:text-muted"
            />
            <span className="ui-mono shrink-0 whitespace-nowrap pr-3 text-[12px] text-muted">@{domain}</span>
          </div>
          <div className="i-ph:arrow-right hidden shrink-0 text-[13px] text-muted sm:block" />
          <input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="you@gmail.com"
            aria-label="Destination email"
            className="ui-input min-w-48 flex-1 py-2 font-geist-mono text-[12.5px]"
          />
          <button
            className="ui-btn-accent shrink-0"
            disabled={createMutation.isPending || !box.trim() || !to.trim()}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? (
              <div className="i-svg-spinners:90-ring-with-bg text-[14px]" />
            ) : (
              <div className="i-ph:plus text-[14px]" />
            )}
            Add
          </button>
        </div>
      </div>
      <p className="mt-2 text-[11.5px] leading-relaxed text-muted">
        Forwarding redirects incoming mail to an inbox you already use — it&apos;s free and receive-only, so replies go
        out from that inbox, not from your domain. Want to send as your domain too? That needs a full mailbox (Google
        Workspace and similar), which isn&apos;t set up here.
      </p>

      <ConfirmationDialog
        isOpen={pendingRemove !== null}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => {
          if (pendingRemove !== null) {
            deleteMutation.mutate(pendingRemove);
          }

          setPendingRemove(null);
        }}
        title="Remove this forward?"
        description={`Mail sent to ${pendingRemove}@${domain} will stop being forwarded right away.`}
        confirmLabel="Remove"
        variant="destructive"
      />
    </Section>
  );
}

const DNS_TYPES = ['A', 'AAAA', 'CNAME', 'ANAME', 'TXT', 'MX', 'SRV'];

/**
 * Editable DNS records for a registered domain. Reads/writes go straight to the
 * registrar via the management endpoints (ids included), unlike the read-only
 * snapshot embedded in the detail payload.
 */
function DnsManager({ domainId }: { domainId: string }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<DomainDnsRecordInput>({ type: 'A', name: '', value: '' });
  // A mis-deleted A/CNAME record takes the site down — confirm first.
  const [pendingDelete, setPendingDelete] = useState<{ id: number; label: string } | null>(null);

  const { data: records, isLoading } = useQuery({
    queryKey: ['domain-dns', domainId],
    queryFn: () => apiListDnsRecords(domainId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['domain-dns', domainId] });

  const createMutation = useMutation({
    mutationFn: (record: DomainDnsRecordInput) => apiCreateDnsRecord(domainId, record),
    onSuccess: () => {
      invalidate();
      setDraft({ type: draft.type, name: '', value: '' });
      toast.success('Record added.');
    },
    onError: (err: unknown) => toast.error(apiErr(err, 'Could not add the record.')),
  });

  const deleteMutation = useMutation({
    mutationFn: (recordId: number) => apiDeleteDnsRecord(domainId, recordId),
    onSuccess: () => {
      invalidate();
      toast.success('Record deleted.');
    },
    onError: (err: unknown) => toast.error(apiErr(err, 'Could not delete the record.')),
  });

  return (
    <Section title="DNS records" icon="i-ph:list-magnifying-glass">
      <div className={classNames(CARD, 'overflow-hidden')}>
        <div className="grid grid-cols-[64px_90px_1fr_auto] items-center gap-3 border-b border-hairline px-5 py-2.5 text-[10px] font-medium uppercase tracking-wide text-faint">
          <span>Type</span>
          <span>Name</span>
          <span>Value</span>
          <span />
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 px-5 py-4 text-[12.5px] text-muted">
            <div className="i-svg-spinners:90-ring-with-bg" />
            Loading records…
          </div>
        )}

        {records?.map((r) => (
          <div
            key={r.id ?? `${r.type}-${r.name}-${r.value}`}
            // Phones: a card per record — type · name · actions, then the full value underneath.
            className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 border-t border-hairline px-4 py-3.5 first:border-t-0 sm:grid-cols-[64px_90px_1fr_auto] sm:gap-3 sm:px-5"
          >
            <span className="w-fit rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-semibold text-accent">
              {r.type}
            </span>
            <span className="ui-mono truncate text-[12.5px] text-secondary">{r.name}</span>
            <span
              className="ui-mono order-last col-span-full min-w-0 break-all text-[12.5px] text-muted sm:order-none sm:col-span-1 sm:truncate"
              title={r.value}
            >
              {r.value}
            </span>
            <div className="flex items-center gap-2">
              <CopyRow value={r.value} />
              {r.id != null && (
                <button
                  onClick={() => setPendingDelete({ id: r.id!, label: `${r.type} ${r.name}` })}
                  disabled={deleteMutation.isPending}
                  className="inline-flex items-center justify-center text-muted transition-colors hover:text-danger touch:h-10 touch:w-10"
                  aria-label={`Delete ${r.type} record ${r.name}`}
                  title="Delete record"
                >
                  <div className="i-ph:trash text-[13px]" />
                </button>
              )}
            </div>
          </div>
        ))}

        {/* Add-record row */}
        <div className="flex flex-wrap items-center gap-2 border-t border-hairline bg-white/[0.02] px-4 py-3.5 sm:px-5">
          <select
            value={draft.type}
            onChange={(e) => setDraft({ ...draft, type: e.target.value })}
            className="ui-input w-auto py-1.5 text-[12.5px]"
            aria-label="Record type"
          >
            {DNS_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="@"
            className="ui-input w-24 py-1.5 font-geist-mono text-[12.5px]"
            aria-label="Record name (host)"
          />
          <input
            value={draft.value}
            onChange={(e) => setDraft({ ...draft, value: e.target.value })}
            placeholder="value"
            className="ui-input min-w-40 flex-1 py-1.5 font-geist-mono text-[12.5px]"
            aria-label="Record value"
          />
          {draft.type === 'MX' && (
            <input
              type="number"
              value={draft.priority ?? ''}
              onChange={(e) => setDraft({ ...draft, priority: e.target.value ? Number(e.target.value) : undefined })}
              placeholder="prio"
              className="ui-input w-20 py-1.5 text-[12.5px]"
              aria-label="Record priority"
            />
          )}
          <button
            className="ui-btn-accent shrink-0"
            disabled={createMutation.isPending || !draft.value.trim()}
            onClick={() => createMutation.mutate({ ...draft, name: draft.name.trim() || '@' })}
          >
            {createMutation.isPending ? (
              <div className="i-svg-spinners:90-ring-with-bg text-[14px]" />
            ) : (
              <div className="i-ph:plus text-[14px]" />
            )}
            Add
          </button>
        </div>
      </div>
      <p className="mt-2 text-[11.5px] leading-relaxed text-muted">
        Changes publish at the registrar immediately; resolvers can take a few minutes to notice. Nameserver (NS)
        records are managed by Dunea.
      </p>

      <ConfirmationDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) {
            deleteMutation.mutate(pendingDelete.id);
          }

          setPendingDelete(null);
        }}
        title="Delete this DNS record?"
        description={`The ${pendingDelete?.label} record is removed at the registrar right away. If your site or email depends on it, it will stop working.`}
        confirmLabel="Delete"
        variant="destructive"
      />
    </Section>
  );
}

/** Title-case a registrar slug (e.g. "spaceship" → "Spaceship") — formatting only. */
function fmtRegistrar(v: string): string {
  return v ? v.charAt(0).toUpperCase() + v.slice(1) : v;
}

function privacyLabel(level: string | null): string | null {
  if (level === 'high') {
    return 'Protected';
  }

  if (level === 'public') {
    return 'Public';
  }

  return null;
}

export function DomainDetailBody({
  domain,
  registrant,
  paymentMethod,
  onResume,
  onRemove,
  busy,
}: {
  domain: ManagedDomain;
  registrant?: RegistrantInfo | null;
  paymentMethod?: PaymentMethodSummary | null;
  onResume?: () => void;
  onRemove?: () => void;
  busy?: boolean;
}) {
  const isPurchased = domain.source === 'purchased' && Boolean(domain.id);
  const isPending = domain.status === 'pending';
  const [tab, setTab] = useState<Tab>('overview');

  // Attaching a domain to a site is paid-only (the server refuses it on free).
  // Everything else here — DNS, renewal, WHOIS, transfer-out, disconnect — manages
  // a domain the user already paid for and stays open on every plan.
  const { isFree } = usePlanAccess();

  const queryClient = useQueryClient();

  const { data: detail } = useQuery({
    queryKey: ['domain', domain.id],
    queryFn: () => apiGetDomain(domain.id as string),
    enabled: isPurchased,
    // Fulfillment states settle in seconds (3s poll); inbound transfers settle in
    // days at the registries — poll those gently.
    refetchInterval: (q) =>
      q.state.data && TERMINAL.has(q.state.data.status)
        ? false
        : q.state.data?.status === 'transferring'
          ? 30000
          : 3000,
  });

  const autoRenewMutation = useMutation({
    mutationFn: (enabled: boolean) => apiSetDomainAutoRenew(domain.id as string, enabled),
    onSuccess: ({ autoRenew }) => {
      queryClient.invalidateQueries({ queryKey: ['domain', domain.id] });
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      toast.success(`Auto-renew turned ${autoRenew ? 'on' : 'off'}.`);
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not update auto-renew.');
    },
  });

  const cardMutation = useMutation({
    mutationFn: apiCreatePaymentMethodUpdateSession,
    onSuccess: ({ url }) => {
      if (url) {
        window.location.href = url;
      } else {
        toast('Manage your card in Account settings → Billing.');
      }
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not update your card. Please try again.');
    },
  });

  const invalidateDomain = () => {
    queryClient.invalidateQueries({ queryKey: ['domain', domain.id] });
    queryClient.invalidateQueries({ queryKey: ['domains'] });
  };

  // Renew-now → checkout redirect; the registrar renewal happens post-payment.
  const renewMutation = useMutation({
    mutationFn: () => apiRenewDomain(domain.id as string),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (err: unknown) => toast.error(apiErr(err, 'Could not start the renewal.')),
  });

  const connectMutation = useMutation({
    mutationFn: (deploymentId: string) => apiConnectDomain(domain.id as string, deploymentId),
    onSuccess: () => {
      invalidateDomain();
      toast.success('Domain connected — SSL provisioning starts now.');
    },
    onError: (err: unknown) => {
      if (handlePlanRequired(err)) {
        return;
      }

      toast.error(apiErr(err, 'Could not connect the domain.'));
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => apiDisconnectDomain(domain.id as string),
    onSuccess: () => {
      invalidateDomain();
      toast.success('Domain disconnected from the app.');
    },
    onError: (err: unknown) => toast.error(apiErr(err, 'Could not disconnect the domain.')),
  });

  const privacyMutation = useMutation({
    mutationFn: (enabled: boolean) => apiSetWhoisPrivacy(domain.id as string, enabled),
    onSuccess: ({ whoisPrivacy }) => {
      invalidateDomain();
      toast.success(`WHOIS privacy ${whoisPrivacy === 'high' ? 'enabled' : 'disabled'}.`);
    },
    onError: (err: unknown) => toast.error(apiErr(err, 'Could not update WHOIS privacy.')),
  });

  const [transferCode, setTransferCode] = useState<{ authCode: string; note: string } | null>(null);

  const transferOutMutation = useMutation({
    mutationFn: () => apiTransferOut(domain.id as string),
    onSuccess: (res) => {
      setTransferCode(res);
      invalidateDomain();
    },
    onError: (err: unknown) => toast.error(apiErr(err, 'Could not set up the transfer. Please try again.')),
  });

  const relockMutation = useMutation({
    mutationFn: () => apiRelockDomain(domain.id as string),
    onSuccess: () => {
      setTransferCode(null);
      invalidateDomain();
      toast.success('Transfer lock re-enabled.');
    },
    onError: (err: unknown) => toast.error(apiErr(err, 'Could not re-lock the domain.')),
  });

  const cancelTransferMutation = useMutation({
    mutationFn: () => apiCancelTransferIn(domain.id as string),
    onSuccess: () => {
      invalidateDomain();
      toast.success('Transfer canceled.');
    },
    onError: (err: unknown) => toast.error(apiErr(err, 'Could not cancel the transfer.')),
  });

  // Deployments for the connect picker — loaded only where the picker renders.
  const settled = isPurchased && ['registered', 'active'].includes(detail?.status ?? domain.status);
  const [pickedDeployment, setPickedDeployment] = useState('');

  const { data: deploymentOptions } = useQuery({
    queryKey: ['deployment-options'],
    queryFn: apiListDeployments,
    enabled: settled,
  });

  const steps = isPurchased && detail ? buildSteps(detail) : [];
  const status = detail?.status ?? domain.status;
  const project = domain.connectedSubdomain;

  // Only compute from real backend values.
  const sslActive = (detail?.sslStatus ?? domain.sslStatus) === 'active';
  const autoRenew = detail?.autoRenew ?? domain.autoRenew;
  const privacy = detail ? privacyLabel(detail.whoisPrivacy) : null;
  const nameservers = detail?.nameservers ?? null;
  const dnsRecords = detail?.dnsRecords ?? null;

  // Tabs — DNS is manageable once the domain is settled; otherwise shown only
  // when we have real registrar data to display.
  const hasDns = settled || Boolean((nameservers && nameservers.length) || (dnsRecords && dnsRecords.length));
  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'overview', label: 'Overview', icon: 'i-ph:squares-four' },
    ...(hasDns ? [{ key: 'dns' as Tab, label: 'DNS', icon: 'i-ph:list-magnifying-glass' }] : []),
    ...(settled ? [{ key: 'email' as Tab, label: 'Email', icon: 'i-ph:envelope-simple' }] : []),
    { key: 'ssl', label: 'SSL', icon: 'i-ph:lock-key' },
    { key: 'deployment', label: 'Deployment', icon: 'i-ph:rocket-launch' },
    { key: 'billing', label: 'Billing', icon: 'i-ph:receipt' },
    { key: 'registrar', label: 'Registrar', icon: 'i-ph:identification-card' },
  ];
  const activeTab = tabs.some((t) => t.key === tab) ? tab : 'overview';

  return (
    <div className="space-y-6">
      {detail?.status === 'failed' && (
        <div className="ui-banner-error items-start">
          <div className="i-ph:warning-circle mt-px shrink-0 text-danger" />
          <span>
            Registration failed{detail.errorMessage ? `: ${detail.errorMessage}` : ''}. Your payment is safe — our team
            can recover this.
          </span>
        </div>
      )}

      {/* Connected (BYO) domain — lighter view */}
      {!isPurchased && (
        <>
          <div className="ui-banner-info items-start">
            <div className="i-ph:link mt-px shrink-0 text-accent" />
            <span>
              This is a domain you own elsewhere, connected to a Dunea project. DNS &amp; SSL are managed automatically.
            </span>
          </div>
          <Section title="Deployment" icon="i-ph:rocket-launch">
            <div className={classNames(CARD, 'divide-y divide-hairline px-5 py-1')}>
              <DetailRow label="Connected project">
                {project ? <span className="ui-mono text-[12.5px]">{project}.dunea.app</span> : 'Not connected'}
              </DetailRow>
            </div>
          </Section>
        </>
      )}

      {isPurchased && !detail && (
        <div className="flex items-center gap-2 text-[13px] text-muted">
          <div className="i-svg-spinners:90-ring-with-bg" />
          Loading…
        </div>
      )}

      {isPurchased && detail && (
        <>
          {/* At a glance — only tiles backed by real data */}
          {/* With three tiles, the odd one out spans the row instead of sitting alone. */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 [&>*:last-child:nth-child(odd)]:col-span-2 lg:[&>*:last-child:nth-child(odd)]:col-span-1">
            <FactTile
              icon={sslActive ? 'i-ph:shield-check' : 'i-ph:shield'}
              label="SSL"
              value={sslActive ? 'Secured' : detail.sslStatus === 'none' ? 'Not set' : 'Provisioning'}
              tone={sslActive ? 'success' : 'accent'}
            />
            <FactTile
              icon="i-ph:arrows-clockwise"
              label="Auto-renew"
              value={autoRenew ? 'On' : 'Off'}
              tone={autoRenew ? 'success' : 'muted'}
            />
            {privacy && <FactTile icon="i-ph:eye-slash" label="WHOIS privacy" value={privacy} tone="success" />}
            <FactTile
              icon="i-ph:plugs-connected"
              label="Project"
              value={project ? 'Connected' : 'Pending'}
              tone={project ? 'accent' : 'muted'}
            />
          </div>

          {/* Tabs */}
          {/* Phones scroll the tab strip sideways instead of wrapping it onto three rows. */}
          <div className="flex gap-1.5 overflow-x-auto rounded-xl border border-hairline bg-raised/50 p-1.5 sm:flex-wrap">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={classNames(
                  'inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3.5 py-2 text-[12.5px] font-medium transition-all duration-200',
                  activeTab === t.key
                    ? 'bg-accent/12 text-accent shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset]'
                    : 'text-muted hover:bg-white/[0.04] hover:text-secondary',
                )}
              >
                <div className={classNames(t.icon, 'text-[14px]')} />
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <div className="grid gap-6 lg:grid-cols-2">
              <Section title="Status timeline" icon="i-ph:path">
                <div className={classNames(CARD, 'px-5 py-5')}>
                  {steps.map((s, i) => (
                    <StepRow key={s.key} step={s} index={i} last={i === steps.length - 1} />
                  ))}
                </div>
              </Section>
              <Section title="Summary" icon="i-ph:info">
                <div className={classNames(CARD, 'divide-y divide-hairline px-5 py-1')}>
                  <DetailRow label="Status">
                    <StatusPill status={status} />
                  </DetailRow>
                  <DetailRow label="Registrar">{fmtRegistrar(detail.registrar)}</DetailRow>
                  <DetailRow label="Registered on">{fmtDate(detail.registeredAt)}</DetailRow>
                  <DetailRow label="Renews on">{fmtDate(detail.expiresAt)}</DetailRow>
                  <DetailRow label="Connected project">
                    {project ? <span className="ui-mono text-[12.5px]">{project}.dunea.app</span> : 'Not connected'}
                  </DetailRow>
                </div>
              </Section>
            </div>
          )}

          {activeTab === 'dns' && (
            <div className="space-y-6">
              {nameservers && nameservers.length > 0 && (
                <Section title="Nameservers" icon="i-ph:hard-drives">
                  <div className={classNames(CARD, 'divide-y divide-hairline')}>
                    {nameservers.map((ns) => (
                      <div key={ns} className="flex items-center gap-3 px-4 py-3.5 sm:px-5">
                        <div className="i-ph:hard-drive text-[15px] text-muted" />
                        <span className="ui-mono min-w-0 flex-1 truncate text-[12.5px] text-secondary">{ns}</span>
                        <CopyRow value={ns} />
                      </div>
                    ))}
                  </div>
                </Section>
              )}
              {settled && <DnsManager domainId={domain.id as string} />}
              {!settled && dnsRecords && dnsRecords.length > 0 && (
                <Section title="DNS records" icon="i-ph:list-magnifying-glass">
                  <div className={classNames(CARD, 'overflow-hidden')}>
                    <div className="hidden grid-cols-[64px_90px_1fr_auto] items-center gap-3 border-b border-hairline px-5 py-2.5 text-[10px] font-medium uppercase tracking-wide text-faint sm:grid">
                      <span>Type</span>
                      <span>Name</span>
                      <span>Value</span>
                      <span />
                    </div>
                    {dnsRecords.map((r, i) => (
                      <div
                        key={`${r.type}-${r.name}-${i}`}
                        className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 border-t border-hairline px-4 py-3.5 first:border-t-0 sm:grid-cols-[64px_90px_1fr_auto] sm:gap-3 sm:px-5"
                      >
                        <span className="w-fit rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-semibold text-accent">
                          {r.type}
                        </span>
                        <span className="ui-mono truncate text-[12.5px] text-secondary">{r.name}</span>
                        <span className="ui-mono order-last col-span-full min-w-0 break-all text-[12.5px] text-muted sm:order-none sm:col-span-1 sm:truncate">
                          {r.value}
                        </span>
                        <CopyRow value={r.value} />
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          )}

          {activeTab === 'email' && settled && (
            <EmailForwardManager domain={detail.domain} domainId={domain.id as string} />
          )}

          {activeTab === 'ssl' && (
            <Section title="SSL certificate" icon="i-ph:lock-key">
              <div className={classNames(CARD, 'p-5')}>
                <div className="mb-5 flex items-center gap-4">
                  <div
                    className={classNames(
                      'flex h-12 w-12 items-center justify-center rounded-xl ring-1 ring-inset',
                      sslActive
                        ? 'bg-success/10 text-success ring-success/20'
                        : 'bg-accent/10 text-accent ring-accent/20',
                    )}
                  >
                    <div
                      className={classNames(
                        sslActive ? 'i-ph:shield-check' : 'i-svg-spinners:90-ring-with-bg',
                        'text-2xl',
                      )}
                    />
                  </div>
                  <div>
                    <div className="text-[14px] font-medium text-primary">
                      {sslActive ? 'HTTPS secured' : detail.sslStatus === 'none' ? 'Not provisioned' : 'Provisioning…'}
                    </div>
                    <div className="mt-0.5 text-[12px] text-muted">Certificate status: {detail.sslStatus}</div>
                  </div>
                </div>
                {(detail.sslIssuer || detail.tlsVersion || detail.sslExpiresAt) && (
                  <div className="divide-y divide-hairline border-t border-hairline">
                    {detail.sslIssuer && <DetailRow label="Issuer">{detail.sslIssuer}</DetailRow>}
                    {detail.tlsVersion && <DetailRow label="TLS version">{detail.tlsVersion}</DetailRow>}
                    {detail.sslExpiresAt && (
                      <DetailRow label="Certificate expires">{fmtDate(detail.sslExpiresAt)}</DetailRow>
                    )}
                  </div>
                )}
              </div>
            </Section>
          )}

          {activeTab === 'deployment' && (
            <Section title="Deployment" icon="i-ph:rocket-launch">
              <div className={classNames(CARD, 'divide-y divide-hairline px-5 py-1')}>
                <DetailRow label="Connected project">
                  {project ? <span className="ui-mono text-[12.5px]">{project}.dunea.app</span> : 'Not connected'}
                </DetailRow>
                {detail.deployUrl && (
                  <DetailRow label="Deployment URL">
                    <span className="ui-mono text-[12px]">{detail.deployUrl.replace(/^https?:\/\//, '')}</span>
                  </DetailRow>
                )}
                <DetailRow label="Status">
                  <StatusPill status={status} />
                </DetailRow>
              </div>

              {settled && (
                <div className={classNames(CARD, 'mt-3 flex flex-wrap items-center gap-2.5 p-4')}>
                  {detail.deploymentId ? (
                    <>
                      <div className="flex min-w-0 flex-1 items-center gap-2 text-[12.5px] text-muted">
                        <div className="i-ph:plugs-connected shrink-0 text-[15px] text-accent" />
                        Serving your app. Disconnect to point it elsewhere.
                      </div>
                      <button
                        className="ui-btn-ghost shrink-0 text-danger hover:bg-danger/10"
                        disabled={disconnectMutation.isPending}
                        onClick={() => disconnectMutation.mutate()}
                      >
                        {disconnectMutation.isPending ? (
                          <div className="i-svg-spinners:90-ring-with-bg text-[14px]" />
                        ) : (
                          <div className="i-ph:plugs text-[14px]" />
                        )}
                        Disconnect
                      </button>
                    </>
                  ) : isFree ? (
                    <>
                      <div className="flex min-w-0 flex-1 items-center gap-2 text-[12.5px] text-muted">
                        <div className="i-ph:lock-simple shrink-0 text-[15px] text-warning" />
                        Connecting a domain to a site needs a paid plan. Your registration is safe either way.
                      </div>
                      <button className="ui-btn-accent shrink-0" onClick={() => showPlans('domain_detail')}>
                        View plans
                      </button>
                    </>
                  ) : (
                    <>
                      <select
                        value={pickedDeployment}
                        onChange={(e) => setPickedDeployment(e.target.value)}
                        className="ui-input min-w-52 flex-1 py-2 text-[12.5px]"
                        aria-label="Choose a project to connect"
                      >
                        <option value="">Choose a live project…</option>
                        {deploymentOptions?.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.subdomain}.dunea.app
                          </option>
                        ))}
                      </select>
                      <button
                        className="ui-btn-accent shrink-0"
                        disabled={connectMutation.isPending || !pickedDeployment}
                        onClick={() => connectMutation.mutate(pickedDeployment)}
                      >
                        {connectMutation.isPending ? (
                          <div className="i-svg-spinners:90-ring-with-bg text-[14px]" />
                        ) : (
                          <div className="i-ph:lightning text-[14px]" />
                        )}
                        Connect
                      </button>
                    </>
                  )}
                </div>
              )}

              {status === 'transferring' && (
                <div className={classNames(CARD, 'mt-3 flex flex-wrap items-center gap-2.5 p-4')}>
                  <div className="flex min-w-0 flex-1 items-center gap-2 text-[12.5px] text-muted">
                    <div className="i-svg-spinners:90-ring-with-bg shrink-0 text-[15px] text-info" />
                    Transfer in progress — your current registrar may email you to approve it. This usually takes a few
                    days.
                  </div>
                  <button
                    className="ui-btn-ghost shrink-0 text-danger hover:bg-danger/10"
                    disabled={cancelTransferMutation.isPending}
                    onClick={() => cancelTransferMutation.mutate()}
                  >
                    Cancel transfer
                  </button>
                </div>
              )}
            </Section>
          )}

          {activeTab === 'billing' && (
            <div className="space-y-6">
              {/* Next renewal — GoDaddy-style: date, itemized amount, auto-renew, card */}
              <Section title="Next renewal" icon="i-ph:calendar-check">
                <div className={classNames(CARD, 'overflow-hidden')}>
                  {/* Auto-renew banner. The toggle is shown only when the payment
                      provider can charge a saved card unattended — otherwise there's
                      no way to auto-renew without billing ourselves, so we surface
                      reminder-based renewal instead. */}
                  {detail.autoRenewSupported ? (
                    <div
                      className={classNames(
                        'flex items-center gap-3 px-5 py-4',
                        autoRenew ? 'bg-success/[0.06]' : 'bg-warning/[0.06]',
                      )}
                    >
                      <div
                        className={classNames(
                          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset',
                          autoRenew
                            ? 'bg-success/10 text-success ring-success/20'
                            : 'bg-warning/10 text-warning ring-warning/20',
                        )}
                      >
                        <div
                          className={classNames(autoRenew ? 'i-ph:arrows-clockwise' : 'i-ph:warning', 'text-[17px]')}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13.5px] font-medium text-primary">
                          Auto-renew is {autoRenew ? 'ON' : 'OFF'}
                        </div>
                        <div className="mt-0.5 text-[12px] text-muted">
                          {autoRenew
                            ? `We'll charge your saved card and renew on ${fmtDate(detail.expiresAt)}. If the payment fails, we'll email you instead.`
                            : `Expires on ${fmtDate(detail.expiresAt)} — turn on auto-renew or renew before then to keep it.`}
                        </div>
                      </div>
                      {/* Toggle — Dunea-managed: charges the saved card, then renews */}
                      <button
                        role="switch"
                        aria-checked={autoRenew}
                        aria-label="Toggle auto-renew"
                        disabled={autoRenewMutation.isPending}
                        onClick={() => autoRenewMutation.mutate(!autoRenew)}
                        // `after:` widens the touch target without changing the switch's size.
                        className={classNames(
                          "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 after:absolute after:-inset-2.5 after:content-['']",
                          autoRenew ? 'bg-success/70' : 'bg-white/[0.12]',
                          autoRenewMutation.isPending && 'opacity-60',
                        )}
                      >
                        <span
                          className={classNames(
                            'inline-block h-4.5 w-4.5 transform rounded-full bg-white shadow transition-transform duration-200',
                            autoRenew ? 'translate-x-[22px]' : 'translate-x-[3px]',
                          )}
                          style={{ height: 18, width: 18 }}
                        />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 bg-warning/[0.06] px-5 py-4">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-warning/10 text-warning ring-1 ring-inset ring-warning/20">
                        <div className="i-ph:bell-ringing text-[17px]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13.5px] font-medium text-primary">Renewal reminders on</div>
                        <div className="mt-0.5 text-[12px] text-muted">
                          Expires on {fmtDate(detail.expiresAt)} — we&apos;ll email you before then so you can renew in
                          one click. (Automatic renewal needs a card we can charge on file, which isn&apos;t available
                          with the current payment method.)
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Renewal amount — single all-in total (markup already included) */}
                  <div className="divide-y divide-hairline px-5">
                    <DetailRow label="Next billing date">{fmtDate(detail.expiresAt)}</DetailRow>
                    {(() => {
                      const total = detail.renewalBreakdown?.total ?? detail.renewPrice;
                      const currency = detail.renewalBreakdown?.currency ?? detail.currency ?? 'usd';

                      return total != null ? (
                        <div className="flex items-center justify-between gap-3 py-3.5">
                          <span className="text-[13px] font-medium text-primary">Renewal price</span>
                          <span className="text-[16px] font-semibold text-primary [font-variant-numeric:tabular-nums]">
                            {fmtMoney(total, currency)}
                            <span className="ml-1 text-[11px] font-normal text-muted">/yr</span>
                          </span>
                        </div>
                      ) : null;
                    })()}
                    {settled && (
                      <div className="flex flex-wrap items-center justify-between gap-3 py-3.5">
                        <span className="text-[12px] text-muted">Extend now — adds a year to the current expiry.</span>
                        <button
                          className="ui-btn-accent shrink-0"
                          disabled={renewMutation.isPending}
                          onClick={() => renewMutation.mutate()}
                        >
                          {renewMutation.isPending ? (
                            <div className="i-svg-spinners:90-ring-with-bg text-[14px]" />
                          ) : (
                            <div className="i-ph:arrows-clockwise text-[14px]" />
                          )}
                          Renew now
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </Section>

              {/* Payment method on file */}
              <Section title="Payment method" icon="i-ph:credit-card">
                <div className={classNames(CARD, 'flex flex-wrap items-center gap-4 p-5')}>
                  {paymentMethod ? (
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent ring-1 ring-inset ring-accent/15">
                        <div className="i-ph:credit-card text-xl" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-medium capitalize text-primary">
                          {paymentMethod.brand} •••• {paymentMethod.last4}
                        </div>
                        <div className="mt-0.5 text-[12px] text-muted">
                          Expires {String(paymentMethod.expMonth).padStart(2, '0')}/
                          {String(paymentMethod.expYear).slice(-2)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex min-w-0 flex-1 items-center gap-2.5 text-[12.5px] text-muted">
                      <div className="i-ph:info shrink-0 text-[15px]" />
                      No saved card — you’ll be prompted to pay at renewal.
                    </div>
                  )}
                  <button
                    className="ui-btn-secondary shrink-0"
                    disabled={cardMutation.isPending}
                    onClick={() => cardMutation.mutate()}
                  >
                    {cardMutation.isPending ? (
                      <div className="i-svg-spinners:90-ring-with-bg text-[14px]" />
                    ) : (
                      <div className="i-ph:pencil-simple text-[14px]" />
                    )}
                    {paymentMethod ? 'Update card' : 'Add card'}
                  </button>
                </div>
              </Section>

              {/* Purchase history */}
              <Section title="Purchase" icon="i-ph:receipt">
                <div className={classNames(CARD, 'divide-y divide-hairline px-5 py-1')}>
                  <DetailRow label="Price paid">{fmtMoney(detail.pricePaid, detail.currency ?? 'usd')}</DetailRow>
                  {detail.registrationYears != null && (
                    <DetailRow label="Term">{detail.registrationYears} year</DetailRow>
                  )}
                  <DetailRow label="Registered on">{fmtDate(detail.registeredAt)}</DetailRow>
                </div>
              </Section>
            </div>
          )}

          {activeTab === 'registrar' && (
            <Section title="Registrar & ownership" icon="i-ph:identification-card">
              <div className={classNames(CARD, 'divide-y divide-hairline px-5 py-1')}>
                <DetailRow label="Registrar">{fmtRegistrar(detail.registrar)}</DetailRow>
                {detail.registrarDomainId && (
                  <DetailRow label="Registrar domain ID">
                    <span className="ui-mono text-[12px]">{detail.registrarDomainId}</span>
                  </DetailRow>
                )}
                {registrant?.hasContact && (
                  <DetailRow label="Registrant">
                    {registrant.firstName} {registrant.lastName}
                  </DetailRow>
                )}
                <DetailRow label="Registered on">{fmtDate(detail.registeredAt)}</DetailRow>
                <DetailRow label="Expires on">{fmtDate(detail.expiresAt)}</DetailRow>
                {privacy && (
                  <DetailRow label="WHOIS privacy">
                    <span className={privacy === 'Protected' ? 'text-success' : 'text-secondary'}>{privacy}</span>
                  </DetailRow>
                )}
                {detail.transferLock != null && (
                  <DetailRow label="Transfer lock">
                    <span className={detail.transferLock ? 'text-success' : 'text-muted'}>
                      {detail.transferLock ? 'Locked' : 'Unlocked'}
                    </span>
                  </DetailRow>
                )}
              </div>

              {settled && (
                <div className="mt-3 space-y-3">
                  {/* WHOIS privacy toggle */}
                  <div className={classNames(CARD, 'flex flex-wrap items-center gap-2.5 p-4')}>
                    <div className="flex min-w-0 flex-1 items-center gap-2 text-[12.5px] text-muted">
                      <div className="i-ph:eye-slash shrink-0 text-[15px] text-accent" />
                      WHOIS privacy hides your contact details from the public registry. Free, on by default.
                    </div>
                    <button
                      className="ui-btn-secondary shrink-0"
                      disabled={privacyMutation.isPending}
                      onClick={() => privacyMutation.mutate(privacy !== 'Protected')}
                    >
                      {privacyMutation.isPending ? (
                        <div className="i-svg-spinners:90-ring-with-bg text-[14px]" />
                      ) : (
                        <div
                          className={classNames(privacy === 'Protected' ? 'i-ph:eye' : 'i-ph:eye-slash', 'text-[14px]')}
                        />
                      )}
                      {privacy === 'Protected' ? 'Make public' : 'Enable privacy'}
                    </button>
                  </div>

                  {/* Transfer out */}
                  <div className={classNames(CARD, 'p-4')}>
                    {transferCode ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-[13px] font-medium text-primary">
                          <div className="i-ph:key text-[15px] text-accent" />
                          Your transfer (EPP) code
                        </div>
                        <div className="flex items-center gap-2 rounded-lg border border-hairline bg-sunken px-3 py-2.5">
                          {/* The user must read or copy the whole code — never truncate it. */}
                          <span className="ui-mono min-w-0 flex-1 select-all break-all text-[13px] text-primary">
                            {transferCode.authCode}
                          </span>
                          <CopyRow value={transferCode.authCode} />
                        </div>
                        <p className="text-[12px] leading-relaxed text-muted">{transferCode.note}</p>
                        <button
                          className="ui-btn-ghost text-[12.5px]"
                          disabled={relockMutation.isPending}
                          onClick={() => relockMutation.mutate()}
                        >
                          {relockMutation.isPending ? (
                            <div className="i-svg-spinners:90-ring-with-bg text-[13px]" />
                          ) : (
                            <div className="i-ph:lock-key text-[13px]" />
                          )}
                          Changed my mind — re-lock the domain
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2.5">
                        <div className="flex min-w-0 flex-1 items-center gap-2 text-[12.5px] text-muted">
                          <div className="i-ph:sign-out shrink-0 text-[15px]" />
                          Moving to another registrar? We&apos;ll unlock the domain and issue your transfer code. Your
                          site keeps working until the transfer completes.
                        </div>
                        <button
                          className="ui-btn-secondary shrink-0"
                          disabled={transferOutMutation.isPending}
                          onClick={() => transferOutMutation.mutate()}
                        >
                          {transferOutMutation.isPending ? (
                            <div className="i-svg-spinners:90-ring-with-bg text-[14px]" />
                          ) : (
                            <div className="i-ph:key text-[14px]" />
                          )}
                          Get transfer code
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Section>
          )}
        </>
      )}

      {/* Actions for pending (unpaid) orders */}
      {isPending && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <button className="ui-btn-accent flex-1 justify-center" disabled={busy} onClick={onResume}>
            <div className="i-ph:credit-card text-[14px]" />
            Complete payment
          </button>
          <button
            className="ui-btn-ghost justify-center text-danger hover:bg-danger/10"
            disabled={busy}
            onClick={onRemove}
          >
            <div className="i-ph:trash text-[14px]" />
            Remove this order
          </button>
        </div>
      )}
    </div>
  );
}
